import { Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import {
  NEXT_PAGE,
  PREVIOUS_PAGE,
  SET_SIGNUP_COMPLETE,
  SET_WIDGET_FINISHED,
  SET_WIDGET_LOADING,
  SET_SIGNUP_NEW_CUSTOMER,
  SET_WIDGET_INITIATED,
  SET_IS_COMPANY_VALID,
  HOUSE_NUMBER,
  POST_CODE,
  HOUSE_NUMBER_ADDITION,
  SET_IS_ADDITIONAL_HOUSE_NUMBER_REQUIRED,
  SET_ACCESS_TOKEN,
  SET_CLIENT_ID,
  CARD_PURPOSE,
  CARD_PURPOSE_ADDITIONAL_INFO,
} from '~/actions/actionTypes';
import { isFinalPage } from '~/utils';
import { ICardSignup, IPage, ISignup } from '~/sharedInterfaces/SharedInterfaces';
import {
  getErrorMessage,
  IComponentWithValidation,
  IErrorMessages,
} from '~/services/ErrorMessageService';
import { setMissingError } from './errorActions';
import { pushToGTM } from '~/actions/gtmActions';
import { IState } from '~/sharedInterfaces/IState';
import {
  ECardRequiredComponents,
  EValidLoanComponentsNL,
  Market,
} from '~/sharedInterfaces/SharedEnums';

const nextPageAction = (currentPage: number) => ({
  type: NEXT_PAGE,
  payload: currentPage + 1,
});

export const setSignupProcessComplete = (completed: boolean) => ({
  type: SET_SIGNUP_COMPLETE,
  payload: completed,
});

export const setSignupProcessNewCustomer = (newCustomer: boolean) => ({
  type: SET_SIGNUP_NEW_CUSTOMER,
  payload: newCustomer,
});

export const setAccessToken = (accessToken?: string) => ({
  type: SET_ACCESS_TOKEN,
  payload: accessToken,
});

export const setIsCompanyValid = (isValid: boolean) => ({
  type: SET_IS_COMPANY_VALID,
  payload: isValid,
});

export const setClientId = (clientId: number) => ({
  type: SET_CLIENT_ID,
  payload: clientId,
});

export const setIsHouseNumberRequired = (isRequired: boolean) => ({
  type: SET_IS_ADDITIONAL_HOUSE_NUMBER_REQUIRED,
  payload: isRequired,
});

export const logUserFirstInteraction = () => {
  return (dispatch: ThunkDispatch<any, any, any>, getState: () => IState) => {
    const { isInitiated, settings } = getState().widgetContext;

    if (!isInitiated) {
      // TODO: Remove duplicate events when UX have confirmed that they're done with restructuring in Google Anayltics
      // The long term solution is to use the event structure where we have the `product` inside the payload, instead of the event name.
      // But we're not fully ready to remove the old event yet, so that's why we're sending "double" events right now.
      dispatch(
        pushToGTM({
          event: `widget-application-initiated`,
          product: settings.widgetType,
        })
      );
      dispatch(
        pushToGTM({
          event: `widget-${settings.widgetType}-application-initiated`,
        })
      );
      dispatch(
        pushToGTM({
          event: `q_user_a_application_initiated`,
        })
      );
      dispatch(
        pushToGTM({
          event: `q_user_a_application_${settings.widgetType}`,
        })
      );
      dispatch({
        type: SET_WIDGET_INITIATED,
        payload: true,
      });
    }
  };
};

export const validatePage = (page: IPage) => {
  return (dispatch: Dispatch, getState: () => IState) => {
    const { error, signup, widgetContext } = getState();
    const { locale, settings } = widgetContext;
    let isPageValid = true;

    Object.keys(page).forEach((componentName: any) => {
      if (settings.widgetType === 'loan') {
        const componentKey = componentName as keyof ISignup;
        if (signup[componentKey] === '') {
          const { missing } = getErrorMessage(locale, componentKey as keyof IErrorMessages);
          dispatch(setMissingError(componentKey, missing));
          isPageValid = false;
        }

        if (
          widgetContext.settings.market === Market.Netherlands &&
          componentKey === EValidLoanComponentsNL.homeAddress &&
          !signup.homeAddress?.postCode
        ) {
          const { missing: postCodeValueMissing } = getErrorMessage(
            locale,
            'postCode' as keyof IErrorMessages
          );
          dispatch(setMissingError(POST_CODE, postCodeValueMissing));
          isPageValid = false;
        }

        if (
          widgetContext.settings.market === Market.Netherlands &&
          componentKey === EValidLoanComponentsNL.homeAddress &&
          widgetContext.isAdditionalHouseNumberRequired &&
          !signup.homeAddress?.houseNumberAddition
        ) {
          const { missing: additionalHouseNUmberValueMissing } = getErrorMessage(
            locale,
            'houseNumberAddition' as keyof IErrorMessages
          );
          dispatch(setMissingError(HOUSE_NUMBER_ADDITION, additionalHouseNUmberValueMissing));
          isPageValid = false;
        }
        if (
          widgetContext.settings.market === Market.Netherlands &&
          componentKey === EValidLoanComponentsNL.homeAddress &&
          !signup.homeAddress?.houseNumber
        ) {
          const { missing: houseNumberValueMissing } = getErrorMessage(
            locale,
            'houseNumber' as keyof IErrorMessages
          );
          dispatch(setMissingError(HOUSE_NUMBER, houseNumberValueMissing));
          isPageValid = false;
        }

        if (error[componentKey as keyof IComponentWithValidation]?.invalid) {
          isPageValid = false;
        }
      } else if (settings.widgetType === 'card') {
        const componentKey = componentName as keyof ICardSignup;
        if (signup[componentKey] === '' && ECardRequiredComponents[componentKey]) {
          const { missing } = getErrorMessage(locale, componentKey as keyof IErrorMessages);
          dispatch(setMissingError(componentKey, missing));
          isPageValid = false;
        }

        if (
          signup.cardPurpose?.applicationReasons?.[0].reason === '' &&
          componentKey === CARD_PURPOSE
        ) {
          const { missing } = getErrorMessage(locale, componentKey as keyof IErrorMessages);
          dispatch(setMissingError(CARD_PURPOSE, missing));
        } else if (
          signup.cardPurpose?.applicationReasons?.[0].reason === 'OtherPurpose' &&
          !signup.cardPurpose?.applicationReasons?.[0].reasonText &&
          componentKey === CARD_PURPOSE
        ) {
          const { missing } = getErrorMessage(locale, CARD_PURPOSE_ADDITIONAL_INFO);

          dispatch(setMissingError('cardPurposeAdditionalInfo', missing));
        }

        if (error[componentKey as keyof IComponentWithValidation]?.invalid) {
          isPageValid = false;
        }
      }
    });

    return isPageValid;
  };
};

const handlePageValidation = () => {
  return (dispatch: ThunkDispatch<any, any, any>, getState: () => IState) => {
    const { widgetContext } = getState();
    const { currentPage, pages } = widgetContext;

    const currentDisplayedPage = pages[currentPage];
    return dispatch(validatePage(currentDisplayedPage));
  };
};

export const nextPage = (shouldSetSignupProccessComplete: boolean = false) => {
  return (dispatch: ThunkDispatch<any, any, any>, getState: () => IState) => {
    const { widgetContext } = getState();
    const shouldContinueToNextPage =
      dispatch(handlePageValidation()) && !isFinalPage(widgetContext);

    if (shouldContinueToNextPage) {
      dispatch(
        pushToGTM({
          event: 'current-page',
          'current-page': widgetContext.currentPage + 1,
        })
      );
      dispatch(nextPageAction(widgetContext.currentPage));
      if (shouldSetSignupProccessComplete) dispatch(setSignupProcessComplete(true));
    }
  };
};

export const setLoading = (loading: boolean) => ({
  type: SET_WIDGET_LOADING,
  payload: loading,
});

export const handleWidgetFinished = () => {
  return (dispatch: ThunkDispatch<any, any, any>, getState: () => IState) => {
    const {
      widgetContext: { pages, currentPage, settings },
    } = getState();
    if (currentPage === pages.length - 1) {
      if (settings.successRedirectUrl) {
        window.location.href = settings.successRedirectUrl;
      } else {
        dispatch({
          type: SET_WIDGET_FINISHED,
          payload: true,
        });
      }
    } else {
      dispatch(nextPage(true));
    }
  };
};

export const previousPage = () => {
  return (dispatch: Dispatch, getState: () => IState) => {
    const { currentPage } = getState().widgetContext;
    if (currentPage > 0) {
      dispatch({
        type: PREVIOUS_PAGE,
        payload: currentPage - 1,
      });
    }
  };
};

// Keep for now incase A/B-test

// export const bringUserToFirstError = () => {
//   return (dispatch: Dispatch, getState: () => IState) => {
//     const { error, widgetContext } = getState();
//     let hasError = false;
//     widgetContext.pages.forEach((page: IPage, currentPage: number) => {
//       if (hasError) return;
//       Object.keys(page).forEach((componentKey) => {
//         if (error[componentKey as keyof IErrorState] && (error[componentKey as keyof IErrorState]?.missing || error[componentKey].invalid)) {
//           hasError = true;
//           dispatch({
//             type: SET_CURRENT_PAGE,
//             payload: currentPage,
//           });
//         }
//       });
//     });
//   };
// };
